// SharedSkillCandidateDetail — /dashboard/platform/shared-skills/candidates/:id
// 候選詳情 + 審核動作（approve / reject / request_changes / redact）。
// Spec §9.3.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, XCircle, MessageSquare, ShieldAlert,
  AlertCircle, Bot, UserCog,
} from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import Skeleton from "../../components/Skeleton.js";
import {
  getCandidate, approveCandidate, rejectCandidate,
  requestChangesCandidate, redactCandidate, friendlySharedSkillsError,
  DOMAIN_LABEL, RISK_LABEL, PRIVACY_LABEL, CANDIDATE_STATUS_LABEL,
  type CandidateDetail,
} from "../../lib/sharedSkillsApi.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type State =
  | { status: "loading" }
  | { status: "ready"; candidate: CandidateDetail }
  | { status: "error"; message: string };

type ActionPanel =
  | null
  | { kind: "approve"; supersedes?: string; review_notes: string }
  | { kind: "reject"; reason: string; review_notes: string }
  | { kind: "request_changes"; notes: string }
  | { kind: "redact_escalate" }
  | { kind: "redact_complete"; notes: string };

interface ParsedSkillJson {
  title?: string; summary?: string; domain?: string; risk_level?: string;
  applicable_scenarios?: string; trigger_conditions?: string;
  recommended_behavior?: string; forbidden_behavior?: string;
  required_checks?: string[]; related_tools?: string[]; examples?: unknown[];
}
interface ParsedAnonymization {
  removed_pii_count?: number; removed_tenant_terms_count?: number;
  by_category?: Record<string, number>; residuals_count?: number;
  privacy_score?: number; privacy_rationale?: string[];
}

export default function SharedSkillCandidateDetail(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [state, setState] = useState<State>({ status: "loading" });
  const [panel, setPanel] = useState<ActionPanel>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setState({ status: "loading" });
    try {
      const r = await getCandidate(id);
      setState({ status: "ready", candidate: r });
    } catch (e) {
      setState({ status: "error", message: friendlySharedSkillsError(e) });
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const parsedSkill: ParsedSkillJson = useMemo(() => {
    if (state.status !== "ready") return {};
    try { return JSON.parse(state.candidate.proposed_skill_json); }
    catch { return {}; }
  }, [state]);

  const parsedAnon: ParsedAnonymization = useMemo(() => {
    if (state.status !== "ready") return {};
    try { return JSON.parse(state.candidate.anonymization_result_json); }
    catch { return {}; }
  }, [state]);

  const doApprove = async () => {
    if (panel?.kind !== "approve") return;
    setBusy(true);
    try {
      const r = await approveCandidate(id, {
        supersedes_public_id: panel.supersedes?.trim() || undefined,
        review_notes: panel.review_notes.trim() || undefined,
      });
      setToast(`已批准為 active skill ${r.public_id} v${r.version}`);
      setPanel(null);
      void load();
    } catch (e) {
      setToast(friendlySharedSkillsError(e));
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (panel?.kind !== "reject") return;
    if (!panel.reason.trim()) { setToast("請填寫拒絕原因"); return; }
    setBusy(true);
    try {
      await rejectCandidate(id, panel.reason.trim(), panel.review_notes.trim() || undefined);
      setToast("已拒絕");
      setPanel(null);
      void load();
    } catch (e) { setToast(friendlySharedSkillsError(e)); }
    finally { setBusy(false); }
  };

  const doRequestChanges = async () => {
    if (panel?.kind !== "request_changes") return;
    if (!panel.notes.trim()) { setToast("請填寫修改說明"); return; }
    setBusy(true);
    try {
      await requestChangesCandidate(id, panel.notes.trim());
      setToast("已要求修改");
      setPanel(null);
      void load();
    } catch (e) { setToast(friendlySharedSkillsError(e)); }
    finally { setBusy(false); }
  };

  const doRedactEscalate = async () => {
    setBusy(true);
    try {
      await redactCandidate(id, { mode: "escalate" });
      setToast("已標為需手動去識別化");
      setPanel(null);
      void load();
    } catch (e) { setToast(friendlySharedSkillsError(e)); }
    finally { setBusy(false); }
  };

  const doRedactComplete = async () => {
    if (panel?.kind !== "redact_complete") return;
    setBusy(true);
    try {
      await redactCandidate(id, {
        mode: "complete",
        overrides: {
          anonymization_notes: panel.notes.trim() || undefined,
          privacy_risk_level: "low",
        },
      });
      setToast("已完成手動去識別化、回到 proposed");
      setPanel(null);
      void load();
    } catch (e) { setToast(friendlySharedSkillsError(e)); }
    finally { setBusy(false); }
  };

  if (state.status === "loading") {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="space-y-3">
        <Link to="/dashboard/platform/shared-skills/candidates" className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> 返回</Link>
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300 flex items-center gap-2"><AlertCircle className="h-4 w-4" />{state.message}</div>
      </div>
    );
  }

  const c = state.candidate;
  const canApprove = c.status === "proposed" || c.status === "reviewing";
  const canReject = !["approved", "rejected"].includes(c.status);
  const canRequestChanges = canApprove;
  const canEscalate = ["proposed", "reviewing", "blocked"].includes(c.status);
  const canComplete = c.status === "needs_manual_redaction";
  const isTerminal = c.status === "approved" || c.status === "rejected";

  return (
    <div className="space-y-4 max-w-4xl">
      <PageHeader
        title={c.proposed_title}
        description={c.proposed_summary}
        actions={
          <Link
            to="/dashboard/platform/shared-skills/candidates"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-surface hover:bg-bg-inset px-3 py-1.5 text-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            返回佇列
          </Link>
        }
      />

      {/* Metadata bar */}
      <div className="rounded-xl border border-border bg-bg-surface p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Cell label="狀態" value={CANDIDATE_STATUS_LABEL[c.status]} />
        <Cell label="domain" value={DOMAIN_LABEL[c.proposed_domain]} />
        <Cell label="風險等級" value={RISK_LABEL[c.proposed_risk_level]} />
        <Cell label="隱私風險" value={PRIVACY_LABEL[c.privacy_risk_level]} />
        <Cell label="quality_score" value={c.quality_score.toFixed(2)} />
        <Cell label="來源" value={c.created_by_type === "ai" ? "AI" : "admin"} icon={c.created_by_type === "ai" ? Bot : UserCog} />
        <Cell label="建立於" value={formatTaipeiDateTime(c.created_at)} />
        <Cell label="最近更新" value={formatTaipeiDateTime(c.updated_at)} />
      </div>

      {/* Structured content */}
      <Section title="提案內容（已去識別化）">
        <Field label="trigger_conditions" body={parsedSkill.trigger_conditions ?? "（缺）"} />
        <Field label="recommended_behavior" body={parsedSkill.recommended_behavior ?? "（缺）"} />
        <Field label="forbidden_behavior" body={parsedSkill.forbidden_behavior ?? "（缺）"} />
        <Field label="required_checks" body={(parsedSkill.required_checks ?? []).join("、") || "（無）"} />
        <Field label="related_tools" body={(parsedSkill.related_tools ?? []).join("、") || "（無）"} />
        <Field label="applicable_scenarios" body={parsedSkill.applicable_scenarios ?? "（缺）"} />
      </Section>

      {/* Anonymization result */}
      <Section title="去識別化結果">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <Cell label="移除 PII 數" value={parsedAnon.removed_pii_count ?? 0} />
          <Cell label="移除租戶識別" value={parsedAnon.removed_tenant_terms_count ?? 0} />
          <Cell label="residuals" value={parsedAnon.residuals_count ?? 0} />
          <Cell label="privacy score" value={(parsedAnon.privacy_score ?? 0).toFixed(2)} />
        </div>
        {parsedAnon.privacy_rationale && parsedAnon.privacy_rationale.length > 0 && (
          <div className="mt-3 text-2xs text-text-muted">
            理由：<ul className="mt-1 list-disc list-inside space-y-0.5">
              {parsedAnon.privacy_rationale.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
      </Section>

      {/* Source summary */}
      {c.source_context_summary && (
        <Section title="來源摘要（已 anonymized）">
          <p className="text-xs text-text-secondary whitespace-pre-wrap">{c.source_context_summary}</p>
          <p className="mt-2 text-2xs text-text-muted">
            來自 {c.source_thread_count} 個 thread / {c.source_event_count} 個事件
            {c.source_tenant_ids_hash_json === null && "（具體租戶 id 隱藏，需 admin:shared_skills:debug 才能展開）"}
          </p>
        </Section>
      )}

      {/* Recent reviews */}
      {c.recent_reviews.length > 0 && (
        <Section title="近期審核紀錄">
          <ul className="text-xs space-y-1.5">
            {c.recent_reviews.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3 border-b border-border last:border-0 pb-1.5">
                <div>
                  <span className="text-text-primary">{r.action}</span>
                  {r.review_notes && <span className="text-text-muted ml-2">— {r.review_notes}</span>}
                </div>
                <span className="text-2xs text-text-muted whitespace-nowrap">{formatTaipeiDateTime(r.created_at)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Action bar */}
      {!isTerminal && (
        <div className="rounded-xl border border-accent/30 bg-bg-surface p-4 space-y-3 sticky bottom-2">
          {!panel && (
            <div className="flex flex-wrap gap-2">
              {canApprove && (
                <button onClick={() => setPanel({ kind: "approve", review_notes: "" })} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 px-3 py-1.5 text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5" /> 批准
                </button>
              )}
              {canRequestChanges && (
                <button onClick={() => setPanel({ kind: "request_changes", notes: "" })} className="inline-flex items-center gap-1.5 rounded-md bg-orange-500/15 hover:bg-orange-500/25 text-orange-300 px-3 py-1.5 text-xs">
                  <MessageSquare className="h-3.5 w-3.5" /> 要求修改
                </button>
              )}
              {canReject && (
                <button onClick={() => setPanel({ kind: "reject", reason: "", review_notes: "" })} className="inline-flex items-center gap-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 px-3 py-1.5 text-xs">
                  <XCircle className="h-3.5 w-3.5" /> 拒絕
                </button>
              )}
              {canEscalate && (
                <button onClick={() => doRedactEscalate()} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 px-3 py-1.5 text-xs disabled:opacity-50">
                  <ShieldAlert className="h-3.5 w-3.5" /> 升級為需手動去識別化
                </button>
              )}
              {canComplete && (
                <button onClick={() => setPanel({ kind: "redact_complete", notes: "" })} className="inline-flex items-center gap-1.5 rounded-md bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 px-3 py-1.5 text-xs">
                  <ShieldAlert className="h-3.5 w-3.5" /> 標記手動去識別完成（回 proposed）
                </button>
              )}
            </div>
          )}

          {panel?.kind === "approve" && (
            <ApprovePanel
              supersedes={panel.supersedes ?? ""}
              notes={panel.review_notes}
              onChangeSupersedes={(v) => setPanel({ ...panel, supersedes: v })}
              onChangeNotes={(v) => setPanel({ ...panel, review_notes: v })}
              onConfirm={doApprove}
              onCancel={() => setPanel(null)}
              busy={busy}
            />
          )}
          {panel?.kind === "reject" && (
            <RejectPanel
              reason={panel.reason} notes={panel.review_notes}
              onChangeReason={(v) => setPanel({ ...panel, reason: v })}
              onChangeNotes={(v) => setPanel({ ...panel, review_notes: v })}
              onConfirm={doReject} onCancel={() => setPanel(null)} busy={busy}
            />
          )}
          {panel?.kind === "request_changes" && (
            <NotesPanel
              title="要求修改說明" placeholder="說明哪裡需要改、為什麼..."
              value={panel.notes}
              onChange={(v) => setPanel({ ...panel, notes: v })}
              onConfirm={doRequestChanges} onCancel={() => setPanel(null)} busy={busy}
            />
          )}
          {panel?.kind === "redact_complete" && (
            <NotesPanel
              title="手動去識別化備註（選填）"
              placeholder="說明做了什麼覆寫..."
              value={panel.notes}
              onChange={(v) => setPanel({ ...panel, notes: v })}
              onConfirm={doRedactComplete} onCancel={() => setPanel(null)} busy={busy}
            />
          )}
        </div>
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 max-w-md rounded-md bg-bg-elevated border border-border px-3 py-2 text-xs shadow-2xl">
          {toast}
          <button onClick={() => setToast(null)} className="ml-2 text-text-muted hover:text-text-primary">×</button>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.ReactElement {
  return (
    <section className="rounded-xl border border-border bg-bg-surface p-4">
      <h3 className="text-xs font-semibold text-text-primary mb-3 uppercase tracking-wide">{title}</h3>
      {children}
    </section>
  );
}

function Cell({ label, value, icon: Icon }: { label: string; value: string | number; icon?: React.ComponentType<{ className?: string }> }): React.ReactElement {
  return (
    <div>
      <div className="text-2xs text-text-muted">{label}</div>
      <div className="mt-0.5 flex items-center gap-1 text-text-primary font-medium">
        {Icon && <Icon className="h-3 w-3" />}
        {value}
      </div>
    </div>
  );
}

function Field({ label, body }: { label: string; body: string }): React.ReactElement {
  return (
    <div className="border-b border-border last:border-0 pb-2 mb-2 last:mb-0">
      <div className="text-2xs font-mono text-text-muted">{label}</div>
      <p className="mt-0.5 text-xs text-text-secondary whitespace-pre-wrap leading-relaxed">{body}</p>
    </div>
  );
}

function ApprovePanel(props: { supersedes: string; notes: string; onChangeSupersedes: (v: string) => void; onChangeNotes: (v: string) => void; onConfirm: () => void; onCancel: () => void; busy: boolean }): React.ReactElement {
  return (
    <div className="space-y-2">
      <div className="text-xs text-text-secondary">批准後將建立新的 active skill 並寫入 Vectorize index。</div>
      <input
        type="text" placeholder="supersedes_public_id（選填；填了會把舊版本標為 deprecated）"
        value={props.supersedes} onChange={(e) => props.onChangeSupersedes(e.target.value)}
        className="w-full rounded-md bg-bg-inset border border-border text-xs px-3 py-1.5 font-mono"
      />
      <textarea
        placeholder="review_notes（選填）" rows={2} value={props.notes}
        onChange={(e) => props.onChangeNotes(e.target.value)}
        className="w-full rounded-md bg-bg-inset border border-border text-xs px-3 py-2"
      />
      <div className="flex gap-2">
        <button onClick={props.onConfirm} disabled={props.busy} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-bg-inset px-3 py-1.5 text-xs font-semibold disabled:opacity-50">
          {props.busy ? "處理中…" : "確認批准"}
        </button>
        <button onClick={props.onCancel} disabled={props.busy} className="rounded-md border border-border bg-bg-inset px-3 py-1.5 text-xs">取消</button>
      </div>
    </div>
  );
}

function RejectPanel(props: { reason: string; notes: string; onChangeReason: (v: string) => void; onChangeNotes: (v: string) => void; onConfirm: () => void; onCancel: () => void; busy: boolean }): React.ReactElement {
  return (
    <div className="space-y-2">
      <input
        type="text" placeholder="拒絕原因（必填）"
        value={props.reason} onChange={(e) => props.onChangeReason(e.target.value)}
        className="w-full rounded-md bg-bg-inset border border-border text-xs px-3 py-1.5"
      />
      <textarea
        placeholder="review_notes（選填）" rows={2} value={props.notes}
        onChange={(e) => props.onChangeNotes(e.target.value)}
        className="w-full rounded-md bg-bg-inset border border-border text-xs px-3 py-2"
      />
      <div className="flex gap-2">
        <button onClick={props.onConfirm} disabled={props.busy} className="inline-flex items-center gap-1.5 rounded-md bg-red-500 hover:bg-red-400 text-bg-inset px-3 py-1.5 text-xs font-semibold disabled:opacity-50">
          {props.busy ? "處理中…" : "確認拒絕"}
        </button>
        <button onClick={props.onCancel} disabled={props.busy} className="rounded-md border border-border bg-bg-inset px-3 py-1.5 text-xs">取消</button>
      </div>
    </div>
  );
}

function NotesPanel(props: { title: string; placeholder: string; value: string; onChange: (v: string) => void; onConfirm: () => void; onCancel: () => void; busy: boolean }): React.ReactElement {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-text-primary">{props.title}</div>
      <textarea
        rows={3} placeholder={props.placeholder} value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md bg-bg-inset border border-border text-xs px-3 py-2"
      />
      <div className="flex gap-2">
        <button onClick={props.onConfirm} disabled={props.busy} className="inline-flex items-center gap-1.5 rounded-md bg-accent hover:bg-accent/80 text-bg-inset px-3 py-1.5 text-xs font-semibold disabled:opacity-50">
          {props.busy ? "處理中…" : "送出"}
        </button>
        <button onClick={props.onCancel} disabled={props.busy} className="rounded-md border border-border bg-bg-inset px-3 py-1.5 text-xs">取消</button>
      </div>
    </div>
  );
}
